import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Dropdown, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import "./TemperatureSensor.scss";
import viewicon from "../../assets/images/view-icon.svg";
import threedotvertical from "../../assets/images/three-dots-vertical.svg";
import play from "../../assets/images/play.svg";
import stop from "../../assets/images/stop-circle.svg";
import areaChartIcon from "../../assets/images/area_chart_icon.svg";
import thermometerIcon from "../../assets/images/thermometer-icon.svg";
import { ReactSVG } from "react-svg";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../redux/hooks";
import useSensors from "../../Hooks/useSensor";
import { toggleClass, truncateText } from "../../utils/commonUtils";
import Loading from "../../components/LoadingPage/Loading";
import { toast } from "react-toastify";
import GraphModel from "../../components/graphModel/graphModel";
import { setCurrentSensor } from "../../redux/sensor/sensorSlice";
import BatteryDisplay from "../../components/BatteryDisplay/BatteryDisplay";
import useRefrigerators from "../../Hooks/useRefrigerators";
import TemperatureDisplayDialog from "../../components/temperatureDisplay/TemperatureDisplayDialog";
import reportIcon from "../../assets/images/report-icon.svg";
import { TemperatureLogReportModel } from "./ManageSensor/TemperatureLogReportModel";
import CommonHeader from "../../components/CommonHeader";

const TemperatureSensor = () => {
  const [sensorsList, setSensorsList] = useState<any[]>([]);
  const [refrigeratorList, setRefrigeratorList] = useState<any[]>([]);
  const [showGraphModel, setShowGraphModel] = useState(false);
  const [selectedSensor, setSelectedSensor] = useState<any>();
  const [isTempDisplayOpen, setIsTempDisplayOpen] = useState<boolean>(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [sortColumn, setSortColumn] = useState<string>("refrigeratorName");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const currentUserId = localStorage.getItem("profileClientId")?.toUpperCase() ?? "";
  const { getRefrigeratorsAPI, getRefrigeratorsLoading } = useRefrigerators();
  const {
    getSensorListAPI,
    getSensorLoading,
    startSensorAPI,
    startSensorLoading,
    stopSensorAPI,
    stopSensorLoading,
  } = useSensors();

  const handleSort = useCallback((column: string) => {
    setSortColumn(prev => {
      if (prev === column) {
        setSortDirection(dir => (dir === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDirection("asc");
      return column;
    });
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchValue(value);
  }, []);

  const handleOpenGraphModal = useCallback((sensor: any) => {
    toggleClass();
    if (sensor) {
      setSelectedSensor(sensor);
      setShowGraphModel(true);
      return;
    }
    toast.error("No temperature logs found for this refrigerator");
  }, [toggleClass]);

  const handleCloseGraphModal = useCallback(() => {
    toggleClass();
    setSelectedSensor(null);
    setShowGraphModel(false);
  }, [toggleClass]);

  const handleOpenTempDisplayModal = useCallback((sensor: any) => {
    setSelectedSensor(sensor);
    setIsTempDisplayOpen(true);
  }, []);

  const handleCloseTempDisplayModal = useCallback(() => {
    setSelectedSensor(null);
    setIsTempDisplayOpen(false);
  }, []);

  const handleOpenReportModal = useCallback((item: any) => {
    toggleClass();
    setSelectedSensor(item);
    setShowReportModal(true);
  }, []);

  const handleCloseReportModal = useCallback(() => {
    toggleClass();
    setSelectedSensor(null);
    setShowReportModal(false);
  }, []);

  const fetchSensorList = useCallback(async () => {
    try {
      const res = await getSensorListAPI({ tenantId: currentUserId });
      setSensorsList(res || []);
    } catch (error) {
      console.error("Failed to get sensor list", error);
      setSensorsList([]);
    }
  }, [currentUserId, getSensorListAPI]);

  const fetchRefrigeratorList = useCallback(async () => {
    try {
      const res = await getRefrigeratorsAPI({ clientCode: currentUserId });
      setRefrigeratorList(res || []);
    } catch (error) {
      console.error("Failed to get refrigerator list", error);
      setRefrigeratorList([]);
    }
  }, [currentUserId, getRefrigeratorsAPI]);

  // Re-fetch sensors when sort changes
  useEffect(() => {
    fetchSensorList();
    fetchRefrigeratorList();
  }, []);

  const handleStartSensor = useCallback(async (sensorId: string) => {
    try {
      const res = await startSensorAPI({ id: sensorId });
      if (res) {
        toast.success(res?.message);
        fetchSensorList();
      }
    } catch (err) {
      console.error("Failed to start sensor", err);
    }
  }, [startSensorAPI, fetchSensorList]);

  const handleStopSensor = useCallback(async (sensorId: string) => {
    try {
      const res = await stopSensorAPI({ id: sensorId });
      if (res) {
        toast.success(res?.message);
        fetchSensorList();
      }
    } catch (err) {
      console.error("Failed to stop sensor", err);
    }
  }, [stopSensorAPI, fetchSensorList]);

  const navigateToView = useCallback((data: any) => {
    dispatch(setCurrentSensor(data));
    navigate("/manage-sensor");
  }, [dispatch, navigate]);

  const renderToolTip = useCallback((data: any) => {
    if (!data) return <span />;

    const emails = data?.email?.split(",").map((e: string) => e.trim()).filter(Boolean) ?? [];
    const contacts = data?.contactNo?.split(",").map((c: string) => c.trim()).filter(Boolean) ?? [];
    const hasData = emails.length > 0 || contacts.length > 0;

    return (
      <Popover id={`alarm-details-${data.id}`} className="ref-alarm-popover">
        <Popover.Header>Alarm Contacts</Popover.Header>
        <Popover.Body>
          {!hasData && <p className="no-contact-data">No contacts configured</p>}
          {emails.length > 0 && (
            <ul className="contact-section">
              <li className="contact-section-label">Email</li>
              {emails.map((e: string) => (
                <li key={e} className="contact-item">{e}</li>
              ))}
            </ul>
          )}
          {contacts.length > 0 && (
            <ul className="contact-section">
              <li className="contact-section-label">Phone</li>
              {contacts.map((c: string) => (
                <li key={c} className="contact-item">{c}</li>
              ))}
            </ul>
          )}
        </Popover.Body>
      </Popover>
    );
  }, []);

  const mergedRefrigeratorList = useMemo(() => {
    if (!refrigeratorList?.length) return [];

    const sensorMap = new Map(sensorsList.map(sensor => [sensor.id, sensor]));

    return refrigeratorList.map(item => ({
      ...item,
      sensor: item.sensorRefrigerator?.sensorId ? sensorMap.get(item.sensorRefrigerator.sensorId) || null : null,
    }));
  }, [refrigeratorList, sensorsList]);

  const filteredRefrigeratorList = useMemo(() => {
    if (!searchValue.trim()) return mergedRefrigeratorList;

    const query = searchValue.toLowerCase().trim();

    return mergedRefrigeratorList.filter((item: any) => {
      const name = String(item.refrigeratorName ?? "").toLowerCase();
      const sensorId = String(item.sensor?.sensorId ?? "").toLowerCase();
      const type = String(item.type ?? "").toLowerCase();
      const placement = String(item.placement ?? "").toLowerCase();
      const tempDisplay = String(item.sensor?.temperatureDisplay ?? "").toLowerCase();
      const intervalDisplay = String(item.sensor?.intervalDisplay ?? "").toLowerCase();
      const alarmStatus = item.sensor ? (item.sensor.alarmActive ? "on" : "off") : "";
      const sensorStatus = String(item.sensor?.sensorStatus ?? "").toLowerCase();
      const batteryLevel = String(item.sensor?.batteryLevel ?? "").toLowerCase();

      return (
        name.includes(query) ||
        sensorId.includes(query) ||
        type.includes(query) ||
        placement.includes(query) ||
        tempDisplay.includes(query) ||
        intervalDisplay.includes(query) ||
        alarmStatus.includes(query) ||
        sensorStatus.includes(query) ||
        batteryLevel.includes(query)
      );
    });
  }, [mergedRefrigeratorList, searchValue]);

  const sortedList = useMemo(() => {
    if (!sortColumn) return filteredRefrigeratorList;

    return [...filteredRefrigeratorList].sort((a: any, b: any) => {
      let aVal: string;
      let bVal: string;

      switch (sortColumn) {
        case "refrigeratorName":
          aVal = String(a.refrigeratorName ?? "").toLowerCase();
          bVal = String(b.refrigeratorName ?? "").toLowerCase();
          break;
        case "sensorId":
          aVal = String(a.sensor?.sensorId ?? "").toLowerCase();
          bVal = String(b.sensor?.sensorId ?? "").toLowerCase();
          break;
        case "type":
          aVal = String(a.type ?? "").toLowerCase();
          bVal = String(b.type ?? "").toLowerCase();
          break;
        case "placement":
          aVal = String(a.placement ?? "").toLowerCase();
          bVal = String(b.placement ?? "").toLowerCase();
          break;
        case "alarmStatus":
          aVal = a.sensor ? (a.sensor.alarmActive ? "on" : "off") : "";
          bVal = b.sensor ? (b.sensor.alarmActive ? "on" : "off") : "";
          break;
        case "sensorStatus":
          aVal = String(a.sensor?.sensorStatus ?? "").toLowerCase();
          bVal = String(b.sensor?.sensorStatus ?? "").toLowerCase();
          break;
        case "batteryLevel":
          // Sort battery as numeric
          const aNum = parseFloat(a.sensor?.batteryLevel ?? 0);
          const bNum = parseFloat(b.sensor?.batteryLevel ?? 0);
          return sortDirection === "asc" ? aNum - bNum : bNum - aNum;
        default:
          aVal = "";
          bVal = "";
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredRefrigeratorList, sortColumn, sortDirection]);

  const isLoading = useMemo(() => {
    return getSensorLoading || getRefrigeratorsLoading;
  }, [getSensorLoading, getRefrigeratorsLoading]);

  return (
    <div className="sensor-listing-wrapper">
      {isLoading ? (<Loading />) : null}
      <div className="sensor-header">
        <CommonHeader
          pageTitle="Coolers"
          searchValue={searchValue}
          handleSearchChange={handleSearch}
        />
      </div>
      <div className="listing-table-wrapper">
        <Table responsive="md" hover={true}>
          <thead>
            <tr>
              <th className="cursor-pointer" onClick={() => handleSort("refrigeratorName")}>
                Cooler Name {sortColumn === "refrigeratorName" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("sensorId")}>
                Sensor Id {sortColumn === "sensorId" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("type")}>
                Cooler Type {sortColumn === "type" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th className="cursor-pointer" onClick={() => handleSort("placement")}>
                Cooler Location {sortColumn === "placement" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th className="text-center">Low/High Temp.</th>
              <th className="text-center">Interval Duration</th>
              <th className="text-center cursor-pointer" onClick={() => handleSort("alarmStatus")}>
                Alarm Status {sortColumn === "alarmStatus" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th className="text-center cursor-pointer" onClick={() => handleSort("sensorStatus")}>
                Status {sortColumn === "sensorStatus" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th className="text-center cursor-pointer" onClick={() => handleSort("batteryLevel")}>
                Battery Level {sortColumn === "batteryLevel" && (sortDirection === "asc" ? "↑" : "↓")}
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedList?.length > 0 ? (
              sortedList.map((item: any) => (
                <tr key={item.id}>
                  <td title={item.refrigeratorName} className="clickable-text" onClick={() => navigateToView(item)}>
                    {item.refrigeratorName}
                  </td>
                  <td title={item.sensor?.sensorId}>{item.sensor?.sensorId}</td>
                  <td title={item?.type}>{item?.type}</td>
                  <td title={item?.placement}>{truncateText(item?.placement, 20)}</td>
                  <td title={item.sensor?.temperatureDisplay} className="text-center">{item.sensor?.temperatureDisplay}</td>
                  <td title={item.sensor?.intervalDisplay} className="text-center">{item.sensor?.intervalDisplay}</td>
                  <td className="text-center">
                    <OverlayTrigger overlay={renderToolTip(item)}>
                      <span className={`tbl-badge ${item.sensor?.alarmActive ? 'active' : ''}`}>
                        {item.sensor ? (item.sensor?.alarmActive ? "On" : "Off") : ""}
                      </span>
                    </OverlayTrigger>
                  </td>
                  <td title={item.sensor?.sensorStatus == null ? "No" : "Yes"} className="text-center">
                    <span className={`tbl-badge ${item.sensor?.sensorStatus !== null ? `active text-${item.sensor?.sensorStatusColor?.toLowerCase()}` : ''}`}>
                      {item.sensor?.sensorStatus !== null ? item.sensor?.sensorStatus : ''}
                    </span>
                  </td>
                  <td title={item.sensor?.batteryLevel} className="text-center">{item.sensor?.batteryLevel ? <BatteryDisplay level={item.sensor?.batteryLevel ?? 0} /> : ''}</td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="link" id="dropdown-basic">
                        <ReactSVG className="svg-box" wrapper="span" src={threedotvertical} />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => navigateToView(item)}>
                          <ReactSVG className="svg-box" wrapper="span" src={viewicon} /> View
                        </Dropdown.Item>
                        {item.sensor && (
                          <>
                            {/* {item.taskId == null ? (
                              <Dropdown.Item onClick={() => handleStartSensor(item.sensor?.id)}>
                                <ReactSVG className="svg-box" wrapper="span" src={play} /> Start Sensor
                              </Dropdown.Item>
                            ) : (
                              <Dropdown.Item onClick={() => handleStopSensor(item.sensor?.id)}>
                                <ReactSVG className="svg-box" wrapper="span" src={stop} /> Stop Sensor
                              </Dropdown.Item>
                            )} */}
                            <Dropdown.Item onClick={() => handleOpenTempDisplayModal(item.sensor)}>
                              <ReactSVG className="svg-box" wrapper="span" src={thermometerIcon} /> Temp. Display
                            </Dropdown.Item>
                          </>
                        )}
                        <Dropdown.Item onClick={() => handleOpenGraphModal(item)}>
                          <ReactSVG className="svg-box" wrapper="span" src={areaChartIcon} /> Temp. Chart
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => handleOpenReportModal(item)}>
                          <ReactSVG className="svg-box" wrapper="span" src={reportIcon} /> Temp. Report
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="text-center py-4 text-muted">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {
        showGraphModel && (
          <GraphModel
            showModal={showGraphModel}
            handleClose={handleCloseGraphModal}
            modelName={`Temperature Log`}
            sensorData={{
              id: selectedSensor?.sensor?.id || "",
              inCentigrade: Boolean(selectedSensor?.sensor?.inCentigrade),
              refId: selectedSensor?.id || "",
            }}
          />
        )
      }

      {
        isTempDisplayOpen && (
          <TemperatureDisplayDialog
            sensorId={selectedSensor?.id}
            isOpen={isTempDisplayOpen}
            handleClose={handleCloseTempDisplayModal}
          />
        )
      }

      {showReportModal && (
        <TemperatureLogReportModel
          showReportModal={showReportModal}
          onCloseReportModal={handleCloseReportModal}
          selectedSensor={
            selectedSensor?.sensor
              ? {
                id: selectedSensor.sensor.id || "",
                serialNumber: selectedSensor.sensor.serialNumber || "",
                sensorName: selectedSensor.sensor.sensorName || "",
                inCentigrade: Boolean(selectedSensor.sensor.inCentigrade),
                lowerTempLimit: selectedSensor.sensor.lowerTempLimit,
                higherTempLimit: selectedSensor.sensor.higherTempLimit,
                refInfo: selectedSensor,
              }
              : null
          }
        />
      )}
    </div >
  );
};
export default TemperatureSensor;
